import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "../bootstrap/bootstrap-5.3.3-dist/css/bootstrap.min.css";
import SnakeBarComponent from "../component/snakeBar";
import API_URL from "../config";
import CourseCard from "./cardCourse";
import './courses.css';

function Courses() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snake, setSnake] = useState({ open: false, message: "", isGood: false });

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/adminPanel/getCourses/all`, {
        method: "GET",
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(isRTL ? "لم يتم العثور على الكورسات (404)." : "Courses not found (404).");
        } else if (response.status === 500) {
          throw new Error(isRTL ? "حدث خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقًا." : "Server error occurred. Please try again later.");
        } else {
          throw new Error(isRTL ? `خطأ غير متوقع (الحالة ${response.status}).` : `Unexpected error (status ${response.status}).`);
        }
      }

      const result = await response.json();

      if (Array.isArray(result?.data?.courses)) {
        setCourses(result.data.courses);
      } else {
        throw new Error(isRTL ? "تنسيق البيانات المستلمة من الخادم غير صالح." : "Invalid data format received from the server.");
      }
    } catch (err) {
      if (err.message.includes("Failed to fetch")) {
        setError(isRTL ? "خطأ في الشبكة. يرجى التحقق من اتصالك." : "Network error. Please check your connection.");
      } else {
        setError(err.message || (isRTL ? "حدث خطأ غير متوقع." : "An unexpected error occurred."));
      }

      setSnake({
        open: true,
        message: isRTL 
          ? "فشل تحميل الكورسات! يرجى التحقق من اتصال الشبكة" 
          : "Failed to load courses! please check your network connection",
        isGood: false,
      });
    } finally {
      setLoading(false);
    }
  }, [isRTL]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  return (
    <>
      <section id="Courses" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Floating Particles */}
        <div className="floating-particles">
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
        </div>

        {/* Header Section */}
        <div className="desc">
          <h2>{isRTL ? 'اكتشف الكورسات الرائعة' : 'Discover Amazing Courses'}</h2>
          <p className="subtitle">
            {isRTL 
              ? 'اختر من بين مجموعة واسعة من الكورسات المصممة خصيصًا لمساعدتك على النجاح' 
              : 'Choose from a wide range of courses designed to help you succeed'
            }
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div className="loading-text">
              {isRTL ? 'جاري تحميل الكورسات الرائعة...' : 'Loading amazing courses...'}
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-container">
            <div className="error-content">
              <h3 style={{color: '#667eea', marginBottom: '1rem'}}>
                {isRTL ? '⚠️ عذراً! حدث خطأ ما' : '⚠️ Oops! Something went wrong'}
              </h3>
              <p>{error}</p>
              <button onClick={fetchCourses} className="retry-button">
                {isRTL ? '🔄 حاول مرة أخرى' : '🔄 Try Again'}
              </button>
            </div>
          </div>
        )}

        {/* Success State */}
        {!loading && !error && (
          <div className="cardDiv">
            {courses.length > 0 ? (
              <>
                {courses.map((course, index) => (
                  <CourseCard 
                    key={course._id || course.title} 
                    course={course}
                    style={{animationDelay: `${index * 0.1}s`}}
                    isRTL={isRTL}
                  />
                ))}
              </>
            ) : (
              <div className="error-container">
                <div className="error-content">
                  <h3 style={{color: '#667eea', marginBottom: '1rem'}}>
                    {isRTL ? '📚 لا توجد كورسات متاحة' : '📚 No Courses Available'}
                  </h3>
                  <p>
                    {isRTL 
                      ? 'نحن نعمل على إضافة كورسات رائعة إلى الفهرس الخاص بنا. تفحص مرة أخرى قريباً!' 
                      : "We're working on adding amazing courses to our catalog. Check back soon!"
                    }
                  </p>
                  <button onClick={fetchCourses} className="retry-button">
                    {isRTL ? '🔄 تحديث' : '🔄 Refresh'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Stats */}
       
      </section>

      {/* Snackbar */}
      {snake.open && (
        <SnakeBarComponent
          open={snake.open}
          message={snake.message}
          isGood={snake.isGood}
          handleClose={() => setSnake({ ...snake, open: false })}
        />
      )}
    </>
  );
}

export default Courses;